// src/components/Admin/TimeConditions.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import BusinessHoursV2 from '../Common/BusinessHoursV2';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useLocation } from 'react-router-dom';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('TimeConditions');

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`time-conditions-tabpanel-${index}`}
      aria-labelledby={`time-conditions-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TimeConditions: React.FC = () => {
  const authOverride = useAuthWithCustomerOverride();
  const { member, isLoginAsCustomer } = authOverride;
  const customerFilter = useCustomerFilter();
  const location = useLocation();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Entity selection states
  const [extensions, setExtensions] = useState<any[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedEntityType, setSelectedEntityType] = useState<'extension' | 'phone_number'>('extension');

  // Get extension or phone number from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const extensionFromUrl = queryParams.get('extension');
  const phoneNumberFromUrl = queryParams.get('phone_number');

  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchEntities();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]);

  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Time Conditions`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) fetchEntities();
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Time Conditions`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  // Set selected entity when data is loaded and entity is in URL
  useEffect(() => {
    if (extensionFromUrl && extensions.length > 0) {
      const extensionExists = extensions.some(ext => ext.extension === extensionFromUrl);
      if (extensionExists) {
        setSelectedEntity(extensionFromUrl);
        setSelectedEntityType('extension');
        setTabValue(0);
      }
    } else if (phoneNumberFromUrl && phoneNumbers.length > 0) {
      const phoneExists = phoneNumbers.some(phone => phone.did === phoneNumberFromUrl);
      if (phoneExists) {
        setSelectedEntity(phoneNumberFromUrl);
        setSelectedEntityType('phone_number');
        setTabValue(1);
      }
    }
  }, [extensionFromUrl, phoneNumberFromUrl, extensions, phoneNumbers]);

  const fetchEntities = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    setLoading(true);
    try {
      const params: any = {};
      
      // Add customer filter for admins - check loginAsCustomer first
      if (isLoginAsCustomer && member?.customerID) {
        logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug(`FILTER MODE - Using customerFilter: ${customerFilter.customerId}`);
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('NO FILTER - Will use JWT customer');
      }
      
      // Fetch extensions
      const extResponse = await api.get('/endpoints/extensions', { params });
      if (extResponse.data.success) {
        const extData = extResponse.data.data;
        // Handle both array and object with extensions property
        if (Array.isArray(extData)) {
          setExtensions(extData);
        } else if (extData && Array.isArray(extData.extensions)) {
          setExtensions(extData.extensions);
        } else {
          setExtensions([]);
        }
      }

      // Fetch phone numbers
      const phoneResponse = await api.get('/endpoints/phone_numbers', { params });
      if (phoneResponse.data.success) {
        // Handle different response formats
        if (Array.isArray(phoneResponse.data.data)) {
          setPhoneNumbers(phoneResponse.data.data);
        } else if (Array.isArray(phoneResponse.data.numbers)) {
          setPhoneNumbers(phoneResponse.data.numbers);
        } else {
          setPhoneNumbers([]);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedEntity('');
    setSelectedEntityType(newValue === 0 ? 'extension' : 'phone_number');
  };

  const handleEntityChange = (event: SelectChangeEvent<string>) => {
    setSelectedEntity(event.target.value);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScheduleIcon color="primary" fontSize="large" />
            <Typography variant="h4" component="h1">
              Time Conditions
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchEntities}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label="Extensions" 
              icon={<ExtensionIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Phone Numbers" 
              icon={<PhoneIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Extension</InputLabel>
            <Select
              value={selectedEntity}
              onChange={handleEntityChange}
              label="Select Extension"
            >
              <MenuItem value="">
                <em>Choose an extension to configure time conditions</em>
              </MenuItem>
              {Array.isArray(extensions) && extensions.map((ext) => (
                <MenuItem key={ext.extension} value={ext.extension}>
                  {ext.extension} - {ext.name || 'Unnamed'}
                  {ext.time_conditions_enabled && (
                    <Chip
                      label="Time Conditions Active"
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Phone Number</InputLabel>
            <Select
              value={selectedEntity}
              onChange={handleEntityChange}
              label="Select Phone Number"
            >
              <MenuItem value="">
                <em>Choose a phone number to configure time conditions</em>
              </MenuItem>
              {Array.isArray(phoneNumbers) && phoneNumbers.map((phone) => (
                <MenuItem key={phone.did} value={phone.did}>
                  {phone.did} - {phone.CallerIDname || 'No caller ID name'}
                  {phone.time_conditions_enabled && (
                    <Chip
                      label="Time Conditions Active"
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TabPanel>

        {selectedEntity && (
          <Box sx={{ mt: 3 }}>
            <BusinessHoursV2
              entityType={selectedEntityType}
              entityId={selectedEntity}
              customerID={(() => {
                // Use the overridden customer ID if in login as mode
                if (isLoginAsCustomer && member?.customerID) {
                  return member.customerID;
                } else if (customerFilter.isFiltered && customerFilter.customerId) {
                  return parseInt(customerFilter.customerId);
                } else {
                  return member?.customerID || 0;
                }
              })()}
            />
          </Box>
        )}

        {!selectedEntity && !loading && (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <ScheduleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom>
              No {selectedEntityType === 'extension' ? 'Extension' : 'Phone Number'} Selected
            </Typography>
            <Typography variant="body2">
              Select an {selectedEntityType === 'extension' ? 'extension' : 'phone number'} from the dropdown above to configure time conditions
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TimeConditions;